import { useEffect } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { ShoppingBag, Zap, Code, CreditCard, BarChart3, Headphones, CheckCircle2, ArrowRight } from 'lucide-react';
import { ShopifyAppsSection } from '../../components/ShopifyAppsSection';
import { ShopifyBadge } from '../../components/PartnerBadges';
import { Navbar } from '../../components/Navbar';
import { SEO, serviceSchema, faqSchema, breadcrumbSchema } from '../../components/SEO';
import { FAQSection } from './PillarPage';

const SHOPIFY_THEME = { primary: '#95BF47', accent: '#5E8E3E', soft: '#F0F7E6' };

const SHOPIFY_FAQS = [
  {
    question: 'How long does a custom Shopify theme build take?',
    answer: 'A typical custom Shopify theme build takes 4 to 8 weeks from kickoff to launch, depending on the scope of design, the number of custom sections, and the depth of integrations required. We provide a detailed timeline at the proposal stage.',
  },
  {
    question: 'How do you price Shopify projects?',
    answer: 'Every project is scoped and priced based on complexity, integrations, and timeline — not a one-size-fits-all rate card. We provide a fixed-scope proposal before any work begins so you know exactly what you are getting and what it costs. Book a free consultation and we will scope it with you.',
  },
  {
    question: 'Can you migrate my store from Magento, WooCommerce, or BigCommerce to Shopify?',
    answer: 'Yes. We handle full Shopify and Shopify Plus migrations including products, customers, orders, content, and historical data. We implement 301 redirects on all legacy URLs to preserve SEO rankings and run a zero-downtime cutover.',
  },
  {
    question: 'Will my SEO rankings drop after migrating to Shopify?',
    answer: 'Not if the migration is done correctly. We map every legacy URL to its new Shopify equivalent with 301 redirects, preserve metadata and structured data, rebuild the sitemap, and submit it to Google Search Console on cutover. Most clients see rankings hold steady or improve within 30 days post-launch.',
  },
  {
    question: 'Should I use a pre-built theme or a custom theme?',
    answer: 'Pre-built themes (Dawn, Sense, Refresh) are the right starting point if you need to launch fast and your brand fits within their visual range — typically $0 to $400 one-time. Custom themes make sense when your brand identity, product complexity, or merchandising strategy goes beyond what theme settings expose. We help you choose honestly based on your goals, not what bills more.',
  },
  {
    question: 'What is Shopify Plus and is it worth the upgrade?',
    answer: 'Shopify Plus is the enterprise tier with checkout customization (Checkout Extensibility), B2B features, multi-storefront support, Shopify Flow automation, dedicated launch support, and lower transaction fees at high volume. It generally pays off above roughly $1M in annual revenue or when you need true checkout control, B2B pricing, or multi-region storefronts.',
  },
  {
    question: 'Do you build custom Shopify apps?',
    answer: 'Yes. We develop both private (custom) and public Shopify apps using Shopify Functions, UI Extensions, the Admin API, and the Storefront API. Common builds include B2B pricing engines, custom fulfillment workflows, and ERP integrations.',
  },
  {
    question: 'Can you customize the Shopify checkout?',
    answer: 'Yes — on Shopify Plus we use Checkout Extensibility (UI Extensions, Shopify Functions for shipping, payment, and discount customization). On standard plans we customize what the platform exposes: thank-you pages, post-purchase upsells, and accelerated checkout flows. We never touch checkout.liquid on legacy stores in ways that block your future Plus upgrade.',
  },
  {
    question: 'Do you integrate Shopify with our ERP, CRM, or accounting system?',
    answer: 'Yes. We integrate Shopify with NetSuite, SAP, Microsoft Dynamics, Zoho, QuickBooks, Xero, Salesforce, HubSpot, and custom in-house systems — handling product, inventory, order, customer, and financial sync with retry logic, error alerting, and reconciliation reporting built in.',
  },
  {
    question: 'Are you a certified Shopify Partner?',
    answer: 'Yes, Smart Solutions is a certified Shopify Expert Partner with experience across Shopify Standard, Advanced, and Plus tiers.',
  },
  {
    question: 'Do you offer ongoing support after launch?',
    answer: 'Yes. We offer monthly retainer plans covering maintenance, feature enhancements, bug fix SLAs, performance monitoring, and priority Slack support for your team. Retainers typically start at 10 hours per month and scale up for stores with active CRO and feature roadmaps.',
  },
  {
    question: 'Who owns the code and the store after launch?',
    answer: 'You do — fully. Code is committed to a Git repository you control, the Shopify store is on your account under your billing, and we hand over all credentials, documentation, and architecture diagrams at the end of every project. No lock-in, no leverage games.',
  },
];

const services = [
  {
    icon: ShoppingBag,
    title: 'Custom Theme Development',
    description: 'Bespoke Shopify themes built from scratch to your exact brand specifications. We craft pixel-perfect storefronts optimized for conversions, mobile performance, and a seamless customer journey — from homepage to checkout.',
    highlights: [
      'Brand-aligned visual design',
      'Mobile-first, performance-optimized',
      'Custom sections & blocks in the Theme Editor',
      'Accessibility & SEO best practices',
    ],
  },
  {
    icon: Zap,
    title: 'Shopify Plus Migrations',
    description: 'Moving from Magento, WooCommerce, BigCommerce, or a legacy custom platform? We handle the full migration with zero downtime — products, customers, orders, SEO URLs, and all historical data preserved.',
    highlights: [
      'Full data migration (products, orders, customers)',
      '301 redirects to protect SEO rankings',
      'Zero-downtime cutover strategy',
      'Post-migration QA and performance audit',
    ],
  },
  {
    icon: Code,
    title: 'Custom App Development',
    description: "When Shopify's app store doesn't have what you need, we build it. From complex B2B pricing engines to custom fulfillment workflows, we develop private and public Shopify apps using the latest Shopify APIs.",
    highlights: [
      'Shopify Functions & UI Extensions',
      'Public & private app development',
      'Third-party API and ERP integrations',
      'Shopify Billing API for app monetization',
    ],
  },
  {
    icon: CreditCard,
    title: 'Payment & Checkout Optimization',
    description: 'The checkout is where revenue is won or lost. We customize the Shopify checkout experience, integrate payment gateways tailored to your region, and reduce friction at every step of the buying journey.',
    highlights: [
      'Custom checkout UI extensions',
      'Payment gateway integrations (regional & global)',
      'Shopify Payments setup & optimization',
      'Buy Now / Express checkout flows',
    ],
  },
  {
    icon: BarChart3,
    title: 'Analytics & Conversion Optimization',
    description: 'We turn your store data into growth. From GA4 and Shopify Analytics setup to A/B testing and heatmap analysis, we identify and fix the friction points costing you conversions.',
    highlights: [
      'GA4 & Shopify Analytics configuration',
      'A/B testing & experimentation',
      'Heatmap & session recording analysis',
      'CRO audits & roadmap recommendations',
    ],
  },
  {
    icon: Headphones,
    title: 'Ongoing Support & Managed Services',
    description: 'Your Shopify store is a living product. We offer flexible retainer plans covering maintenance, feature enhancements, bug fixes, performance monitoring, and priority support whenever you need it.',
    highlights: [
      '24/7 uptime and performance monitoring',
      'Monthly feature releases & iterations',
      'Priority bug fix SLAs',
      'Dedicated Slack channel for your team',
    ],
  },
];

const process = [
  { step: '01', label: 'Discovery', desc: 'We audit your current store, understand your goals, and map out the full scope.' },
  { step: '02', label: 'Design', desc: 'Wireframes and high-fidelity mockups reviewed and approved before a line of code is written.' },
  { step: '03', label: 'Build', desc: 'Development in a staging environment with regular client reviews and feedback cycles.' },
  { step: '04', label: 'Launch', desc: 'Zero-downtime deployment, full QA pass, and post-launch monitoring.' },
  { step: '05', label: 'Grow', desc: 'Ongoing support, A/B testing, and iterative improvements to keep scaling.' },
];

export const ShopifyServicesPage = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen bg-white flex flex-col">
      <SEO
        title="Shopify Development & Shopify Plus Experts"
        description="Certified Shopify Expert Partners. We design custom themes, build private and public Shopify apps, migrate stores to Shopify Plus, and optimize checkout and conversion."
        path="/services/shopify"
        jsonLd={[
          serviceSchema({
            name: 'Shopify Development Services',
            description:
              'Full-stack Shopify development including custom theme design, Shopify Plus migrations, custom app development, checkout optimization, and ongoing managed support from a certified Shopify Expert Partner.',
            path: '/services/shopify',
          }),
          faqSchema(SHOPIFY_FAQS),
          breadcrumbSchema([
            { name: 'Home', path: '/' },
            { name: 'Services', path: '/#services' },
            { name: 'Shopify Development', path: '/services/shopify' },
          ]),
        ]}
      />
      <Navbar />

      {/* Hero */}
      <section className="relative overflow-hidden bg-white border-b border-[#E2E8F0]">
        <div className="absolute top-0 right-0 w-[600px] h-[600px] rounded-full bg-[#95BF47]/6 blur-[130px] pointer-events-none" />
        <div className="container-custom py-20 lg:py-28 relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.65 }}
          >
            <div className="flex items-center gap-3 mb-6">
              <span className="section-label" style={{ color: '#5E8E3E' }}>Shopify Development</span>
              <span className="w-6 h-px bg-[#95BF47]/40" />
            </div>
            <h1
              className="font-display font-bold text-[#0F172A] mb-6 leading-tight"
              style={{ fontSize: 'clamp(2.5rem, 5vw, 4.5rem)' }}
            >
              Your Trusted{' '}
              <span className="italic" style={{ color: '#95BF47' }}>Shopify</span>
              <br />Expert Partner
            </h1>
            <p className="text-[#374151] text-lg max-w-2xl leading-relaxed mb-8">
              As certified Shopify experts, we design, build, and scale e-commerce stores that convert.
              From bespoke themes to Shopify Plus migrations and custom app development — we cover the full stack.
            </p>
            <div className="flex flex-wrap gap-3 mb-10">
              <ShopifyBadge />
              <span className="inline-flex items-center gap-2 px-3 py-1.5 rounded-md border border-[#2563EB]/25 text-[#2563EB] bg-white text-[0.7rem] font-medium">
                <span className="w-1.5 h-1.5 rounded-full bg-[#2563EB]" />
                Shopify Plus Specialists
              </span>
            </div>
            <Link
              to="/book"
              className="group inline-flex items-center gap-2 px-7 py-3.5 bg-[#95BF47] text-white text-sm font-semibold hover:bg-[#7EA73D] transition-colors duration-300 shadow-sm"
            >
              Start Your Project
              <ArrowRight size={15} className="group-hover:translate-x-1 transition-transform" />
            </Link>
          </motion.div>
        </div>
      </section>

      {/* Services */}
      <section className="section-padding bg-[#F8F7F4]">
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
            className="mb-12"
          >
            <div className="flex items-center gap-3 mb-4">
              <span className="section-label" style={{ color: '#5E8E3E' }}>What We Offer</span>
              <span className="w-6 h-px bg-[#95BF47]/40" />
            </div>
            <h2
              className="font-display font-bold text-[#0F172A]"
              style={{ fontSize: 'clamp(1.75rem, 3.5vw, 2.75rem)' }}
            >
              Everything Your Shopify Store Needs
            </h2>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-px bg-[#E2E8F0]">
            {services.map((service, i) => {
              const Icon = service.icon;
              return (
                <motion.div
                  key={service.title}
                  initial={{ opacity: 0, y: 16 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.4, delay: i * 0.07 }}
                  className="group bg-white p-8 hover:bg-[#F0F7E6] transition-colors duration-300 relative overflow-hidden"
                >
                  <div className="absolute top-0 left-0 right-0 h-[2px] bg-[#95BF47] scale-x-0 group-hover:scale-x-100 transition-transform duration-300 origin-left" />
                  <div className="w-10 h-10 flex items-center justify-center mb-5 border border-[#95BF47]/20 bg-[#F0F7E6]">
                    <Icon size={18} style={{ color: '#95BF47' }} />
                  </div>
                  <h3 className="text-[#0F172A] font-semibold mb-3 group-hover:text-[#5E8E3E] transition-colors">
                    {service.title}
                  </h3>
                  <p className="text-[#374151] text-sm leading-relaxed mb-5">
                    {service.description}
                  </p>
                  <ul className="space-y-2">
                    {service.highlights.map((h) => (
                      <li key={h} className="flex items-start gap-2 text-xs text-[#374151]">
                        <CheckCircle2 size={13} className="shrink-0 mt-0.5" style={{ color: '#95BF47' }} />
                        {h}
                      </li>
                    ))}
                  </ul>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Process */}
      <section className="section-padding bg-white border-t border-b border-[#E2E8F0]">
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
            className="mb-12"
          >
            <div className="flex items-center gap-3 mb-4">
              <span className="section-label" style={{ color: '#5E8E3E' }}>How We Work</span>
              <span className="w-6 h-px bg-[#95BF47]/40" />
            </div>
            <h2
              className="font-display font-bold text-[#0F172A]"
              style={{ fontSize: 'clamp(1.75rem, 3.5vw, 2.75rem)' }}
            >
              Our Delivery Process
            </h2>
          </motion.div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-px bg-[#E2E8F0]">
            {process.map((p, i) => (
              <motion.div
                key={p.step}
                initial={{ opacity: 0, y: 12 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.4, delay: i * 0.08 }}
                className="bg-white p-6 relative"
              >
                <span className="font-mono text-[0.58rem] tracking-widest text-[#9CA3AF] block mb-3">{p.step}</span>
                <h4 className="font-semibold text-[#0F172A] mb-2 text-sm">{p.label}</h4>
                <p className="text-[#374151] text-xs leading-relaxed">{p.desc}</p>
                {i < process.length - 1 && (
                  <span className="hidden lg:block absolute right-0 top-1/2 -translate-y-1/2 translate-x-1/2 text-[#9CA3AF] text-xs z-10">→</span>
                )}
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <FAQSection faqs={SHOPIFY_FAQS} theme={SHOPIFY_THEME} title="Shopify Development FAQs" />

      {/* Shopify Apps */}
      <ShopifyAppsSection />

      {/* CTA */}
      <section className="relative overflow-hidden bg-[#0F172A]">
        <div className="absolute top-0 left-1/4 w-[500px] h-[500px] rounded-full bg-[#95BF47]/10 blur-[120px] pointer-events-none" />
        <div className="container-custom py-24 relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <h2
              className="font-display font-bold text-white mb-4"
              style={{ fontSize: 'clamp(2rem, 4vw, 3.5rem)' }}
            >
              Ready to Build Your{' '}
              <span className="italic" style={{ color: '#95BF47' }}>Dream Store?</span>
            </h2>
            <p className="text-[#94A3B8] max-w-xl mb-8 leading-relaxed">
              Book a free consultation and let's talk about your Shopify project.
              No commitment — just a conversation.
            </p>
            <div className="flex flex-wrap gap-4">
              <Link
                to="/book"
                className="group inline-flex items-center gap-2 px-7 py-3.5 bg-[#95BF47] text-white text-sm font-semibold hover:bg-[#7EA73D] transition-colors duration-300"
              >
                Book Free Consultation
                <ArrowRight size={15} className="group-hover:translate-x-1 transition-transform" />
              </Link>
              <Link
                to="/#contact"
                className="inline-flex items-center gap-2 px-7 py-3.5 border border-white/20 text-white text-sm font-medium hover:border-white/40 hover:bg-white/5 transition-all duration-300"
              >
                Contact Us
              </Link>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Footer strip */}
      <div className="bg-[#0F172A] border-t border-[#1E40AF] py-6">
        <div className="container-custom flex flex-col sm:flex-row items-center justify-between gap-3">
          <p className="font-mono text-[0.55rem] tracking-widest text-[#334155] uppercase">
            © {new Date().getFullYear()} Smart Solutions · All Rights Reserved
          </p>
          <Link to="/" className="font-mono text-[0.55rem] tracking-widest text-[#334155] hover:text-[#94A3B8] uppercase transition-colors">
            ← Back to main site
          </Link>
        </div>
      </div>

    </div>
  );
};
